import React from "react";
import { Table, Tag, Input, Select, Space, Typography } from "antd";
import {
    faList, faTriangleExclamation, faServer, faUserSlash,
} from "@fortawesome/free-solid-svg-icons";
import { fmtUnix, defaultRange } from "./log-utils";
import { useLogTable } from "./hooks/use-log-table";
import LogStatCards from "./components/log-stat-cards";
import LogToolbar from "./components/log-toolbar";
import LogFilterSelect from "./components/log-filter-select";
import LogIpFilter from "./components/log-ip-filter";
import LogTimelineChart from "./components/log-timeline-chart";
import ErrorGroupPanel from "./components/error-group-panel";
import LogDetailDrawer from "./components/log-detail-drawer";
import logApi from "../../../api/log-api";

const { Paragraph } = Typography;

// Tab xem ErrorLog — mọi lỗi (HTTP + nền) đã ghi DB. Filter + phân trang + drawer.
// Stat card phản ánh đúng bộ lọc hiện tại (kể cả khoảng ngày).
// options = { error_type: [...], method: [...] } lấy từ DB, do trang cha truyền xuống.
function ErrorLogTab({ onRefreshStats, options }) {
    const t = useLogTable({
        name: "ErrorLog",
        initialFilter: {
            error_type: "", status_code: "", is_critical: "", method: "", ip: "", q: "",
            ...defaultRange(),
        },
        load: (p) => logApi.getErrors(p),
        remove: (ids) => logApi.deleteErrors(ids),
        removeAll: (f) => logApi.deleteAllErrors(f),
        loadStats: (f) => logApi.getErrorStats(f),
        exportAll: (f) => logApi.exportCsv("errors", f),
        onMutated: onRefreshStats,
    });

    const s = t.stats;
    const cards = [
        { key: "total", title: "Tổng lỗi", value: s?.total, icon: faList, iconColor: "text-blue-600", bgColor: "bg-blue-50" },
        { key: "critical", title: "Critical", value: s?.critical, icon: faTriangleExclamation, iconColor: "text-red-600", bgColor: "bg-red-50" },
        { key: "server", title: "Lỗi 5xx", value: s?.server, icon: faServer, iconColor: "text-orange-500", bgColor: "bg-orange-50" },
        { key: "client", title: "Lỗi 4xx", value: s?.client, icon: faUserSlash, iconColor: "text-purple-600", bgColor: "bg-purple-50" },
    ];

    const columns = [
        { title: "Thời gian", dataIndex: "created_at", width: 150, render: fmtUnix },
        {
            title: "Status", dataIndex: "status_code", width: 80,
            render: (c) => <Tag color={c >= 500 ? "red" : c >= 400 ? "orange" : "default"}>{c}</Tag>,
        },
        {
            title: "Loại", dataIndex: "error_type", width: 130,
            render: (tp, r) => (
                <Space direction="vertical" size={0}>
                    <span>{tp}</span>
                    {r.is_critical ? <Tag color="red">critical</Tag> : null}
                </Space>
            ),
        },
        { title: "Method", dataIndex: "method", width: 80 },
        { title: "Path", dataIndex: "path", width: 200, ellipsis: true },
        { title: "Message", dataIndex: "message", ellipsis: true },
        { title: "User", dataIndex: "user_id", width: 80 },
        { title: "IP", dataIndex: "ip_address", width: 130 },
    ];

    return (
        <div>
            <LogStatCards cards={cards} loading={t.statsLoading} />
            <LogToolbar t={t} onRefresh={() => { t.reload(); onRefreshStats?.(); }}>
                <LogFilterSelect
                    width={170} placeholder="Loại lỗi" values={options?.error_type}
                    value={t.f.error_type} onChange={(v) => t.setField("error_type", v)}
                />
                <LogFilterSelect
                    width={110} placeholder="Method" values={options?.method}
                    value={t.f.method} onChange={(v) => t.setField("method", v)}
                />
                <Input
                    style={{ width: 110 }} placeholder="Status code" allowClear
                    inputMode="numeric"
                    value={t.f.status_code}
                    // Chỉ nhận chữ số — gõ chữ vào đây trước đây sinh query hỏng.
                    onChange={(e) => t.setField("status_code", e.target.value.replace(/\D/g, ""))}
                />
                <Select
                    style={{ width: 120 }} allowClear placeholder="Critical"
                    value={t.f.is_critical || undefined}
                    onChange={(v) => t.setField("is_critical", v || "")}
                    options={[{ value: "1", label: "Critical" }, { value: "0", label: "Thường" }]}
                />
                <LogIpFilter
                    value={t.f.ip}
                    onChange={(v) => t.setField("ip", v)}
                    onSearch={t.search}
                />
                <Input.Search
                    style={{ width: 240 }} placeholder="Tìm path / message" allowClear
                    value={t.f.q}
                    onChange={(e) => t.setField("q", e.target.value)}
                    onSearch={t.search}
                />
            </LogToolbar>

            <LogTimelineChart type="errors" filter={t.appliedF} reloadKey={t.version} color="#ff4d4f" />

            {/* Bấm 1 nhóm → lọc bảng chi tiết theo đúng path + status của nhóm đó */}
            <ErrorGroupPanel
                filter={t.appliedF}
                reloadKey={t.version}
                onPick={(g) => t.applyFilter({ q: g.path || "", status_code: g.status_code ?? "" })}
            />

            <Table
                rowKey="id"
                size="small"
                loading={t.loading}
                columns={columns}
                dataSource={t.rows}
                scroll={{ x: 1100 }}
                rowSelection={{ selectedRowKeys: t.selected, onChange: t.setSelected }}
                onRow={(r) => ({ onClick: () => t.openDetail(r), style: { cursor: "pointer" } })}
                expandable={{
                    expandedRowRender: (r) => (
                        <div>
                            <Paragraph style={{ marginBottom: 4 }}><b>Message:</b> {r.message}</Paragraph>
                            <Paragraph style={{ marginBottom: 0 }}><b>Module:</b> {r.module || "-"}</Paragraph>
                            {r.stack ? (
                                <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, marginTop: 8, maxHeight: 320, overflow: "auto" }}>
                                    {r.stack}
                                </pre>
                            ) : null}
                        </div>
                    ),
                }}
                pagination={{
                    current: t.page, pageSize: t.limit, total: t.total,
                    showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200],
                    showTotal: (n) => `${n} dòng`,
                    onChange: t.onPageChange, onShowSizeChange: t.onPageChange,
                }}
            />

            <LogDetailDrawer row={t.detail} title="Chi tiết lỗi" onClose={t.closeDetail} />
        </div>
    );
}

export default ErrorLogTab;
